import {engineContext} from './context.js';

(function(global){
'use strict';

const VERSION='0.1.0-alpha';

const STAGE_ORDER=['BUSINESS','STOCK','TIMING','WHY_WRONG'];
const PRIORITY_ORDER={NONE:0,LOW:1,REVIEW:2,MEDIUM:3,HIGH:4,CRITICAL:5};

function rankPriority(x){return PRIORITY_ORDER[x]||0}

function normalizeItem(x){
  x=x||{};
  return Object.freeze({
    symbol:x.symbol||null,
    scope:['OWNED','WATCHLIST','RESEARCH'].includes(x.scope)?x.scope:'RESEARCH',
    priority:Object.prototype.hasOwnProperty.call(PRIORITY_ORDER,x.priority)?x.priority:'NONE',
    reasons:Object.freeze(Array.isArray(x.reasons)?x.reasons.slice(0,6):[]),
    companyState:x.companyState||'UNKNOWN',
    valuationState:x.valuationState||'UNKNOWN',
    timingState:x.timingState||'UNKNOWN',
    contradictionState:x.contradictionState||'NONE',
    verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED',
    freshnessHours:typeof x.freshnessHours==='number'&&Number.isFinite(x.freshnessHours)?Math.max(0,x.freshnessHours):null
  });
}

function routeStage(input){
  const x=normalizeItem(input);

  if(x.contradictionState && x.contradictionState!=='NONE' && x.contradictionState!=='NO_MAJOR_CONTRADICTION'){
    return 'WHY_WRONG';
  }
  if(x.companyState==='UNKNOWN'||x.companyState==='WEAK'||x.companyState==='DETERIORATING'){
    return 'BUSINESS';
  }
  if(x.valuationState==='UNKNOWN'||x.valuationState==='EXPENSIVE'||x.valuationState==='REVIEW'){
    return 'STOCK';
  }
  if(x.timingState==='UNKNOWN'||x.timingState==='RISK_ELEVATED'||x.timingState==='REVIEW'){
    return 'TIMING';
  }
  return 'WHY_WRONG';
}

function taskType(input){
  const x=normalizeItem(input);
  const stage=routeStage(x);

  if(x.verification==='UNVERIFIED') return 'VERIFY_EVIDENCE';
  if(stage==='BUSINESS') return 'REVIEW_BUSINESS';
  if(stage==='STOCK') return 'REVIEW_VALUATION';
  if(stage==='TIMING') return 'REVIEW_TIMING';
  return 'CHALLENGE_THESIS';
}

function urgency(input){
  const x=normalizeItem(input);
  if(x.priority==='CRITICAL') return 'NOW';
  if(x.priority==='HIGH') return x.scope==='OWNED'?'NOW':'SOON';
  if(x.priority==='MEDIUM') return 'SOON';
  if(x.priority==='REVIEW') return 'WHEN_RELEVANT';
  return 'BACKLOG';
}

function buildTask(input){
  const x=normalizeItem(input);
  const stage=routeStage(x);
  const type=taskType(x);

  return Object.freeze({
    symbol:x.symbol,
    scope:x.scope,
    priority:x.priority,
    urgency:urgency(x),
    stage,
    taskType:type,
    reasons:x.reasons,
    verification:x.verification,
    done:false,
    tradeSignal:false,
    capitalPermission:false,
    companyQualityOverride:false
  });
}

function dedupe(tasks){
  const rows=Array.isArray(tasks)?tasks:[];
  const best=new Map();

  for(const t0 of rows){
    const t=buildTask(t0);
    const key=`${t.symbol||'UNKNOWN'}|${t.stage}|${t.taskType}`;
    const existing=best.get(key);
    if(!existing || rankPriority(t.priority)>rankPriority(existing.priority)){
      best.set(key,t);
    }
  }
  return Object.freeze([...best.values()]);
}

function buildQueue(items){
  const rows=Array.isArray(items)?items:[];
  const tasks=dedupe(rows);

  const sorted=tasks.slice().sort((a,b)=>{
    const ownedDelta=(b.scope==='OWNED'?1:0)-(a.scope==='OWNED'?1:0);
    if(ownedDelta!==0) return ownedDelta;
    const p=rankPriority(b.priority)-rankPriority(a.priority);
    if(p!==0) return p;
    return STAGE_ORDER.indexOf(a.stage)-STAGE_ORDER.indexOf(b.stage);
  });

  return Object.freeze(sorted);
}

function nextTask(items){
  const q=buildQueue(items);
  return q.length?q[0]:null;
}

function summary(items,maxItems){
  const max=typeof maxItems==='number'&&Number.isFinite(maxItems)?Math.max(1,Math.floor(maxItems)):5;
  const q=buildQueue(items);
  const visible=q.filter(t=>t.priority!=='NONE'&&t.priority!=='LOW');

  return Object.freeze({
    next:visible[0]||null,
    top:Object.freeze(visible.slice(0,max)),
    hiddenCount:Math.max(0,visible.length-max),
    counts:Object.freeze({
      total:q.length,
      business:q.filter(t=>t.stage==='BUSINESS').length,
      stock:q.filter(t=>t.stage==='STOCK').length,
      timing:q.filter(t=>t.stage==='TIMING').length,
      whyWrong:q.filter(t=>t.stage==='WHY_WRONG').length
    }),
    tradeSignal:false
  });
}

function stageHeadline(task){
  if(!task) return 'No research task currently needs attention.';
  const label={
    BUSINESS:'Business first',
    STOCK:'Stock/valuation next',
    TIMING:'Timing/context next',
    WHY_WRONG:'Challenge the thesis'
  }[task.stage]||task.stage;

  return `${label}: ${task.symbol||'Unknown company'}`;
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const weakOwned={
    symbol:'AAA',scope:'OWNED',priority:'HIGH',
    companyState:'WEAK',valuationState:'ATTRACTIVE',timingState:'OPEN',
    contradictionState:'NONE',verification:'VERIFIED',
    reasons:['WEAK_COMPANY']
  };
  const expensiveWatch={
    symbol:'BBB',scope:'WATCHLIST',priority:'HIGH',
    companyState:'STRONG',valuationState:'EXPENSIVE',timingState:'OPEN',
    contradictionState:'NONE',verification:'VERIFIED'
  };
  const timingOwned={
    symbol:'CCC',scope:'OWNED',priority:'MEDIUM',
    companyState:'STRONG',valuationState:'ATTRACTIVE',timingState:'RISK_ELEVATED',
    contradictionState:'NONE',verification:'VERIFIED'
  };
  const contradictionOwned={
    symbol:'DDD',scope:'OWNED',priority:'CRITICAL',
    companyState:'STRONG',valuationState:'ATTRACTIVE',timingState:'OPEN',
    contradictionState:'STRONG_COMPANY_EXTERNAL_PRESSURE',verification:'VERIFIED'
  };
  const unverified={
    symbol:'EEE',scope:'WATCHLIST',priority:'REVIEW',
    companyState:'STRONG',valuationState:'ATTRACTIVE',timingState:'OPEN',
    contradictionState:'NONE',verification:'UNVERIFIED'
  };

  ok('weak company routes to BUSINESS',routeStage(weakOwned)==='BUSINESS');
  ok('expensive stock routes to STOCK',routeStage(expensiveWatch)==='STOCK');
  ok('timing risk routes to TIMING',routeStage(timingOwned)==='TIMING');
  ok('contradiction routes to WHY_WRONG',routeStage(contradictionOwned)==='WHY_WRONG');
  ok('unverified item becomes verify task',taskType(unverified)==='VERIFY_EVIDENCE');

  let task=buildTask(contradictionOwned);
  ok('critical owned task urgency is NOW',task.urgency==='NOW');
  ok('task remains non-trading',task.tradeSignal===false);
  ok('task grants no capital permission',task.capitalPermission===false);
  ok('task never overrides company quality',task.companyQualityOverride===false);

  const q=buildQueue([expensiveWatch,timingOwned,weakOwned,contradictionOwned,unverified]);
  ok('owned items rank before watchlist',q[0].scope==='OWNED');
  ok('critical owned contradiction becomes next task',q[0].symbol==='DDD');

  const dupes=dedupe([
    {...weakOwned,priority:'MEDIUM'},
    {...weakOwned,priority:'HIGH'}
  ]);
  ok('duplicate task is compressed',dupes.length===1);
  ok('dedupe preserves highest priority',dupes[0].priority==='HIGH');

  const s=summary([expensiveWatch,timingOwned,weakOwned,contradictionOwned,unverified],3);
  ok('summary caps visible top list',s.top.length===3);
  ok('summary reports hidden actionable tasks',s.hiddenCount>=1);
  ok('summary counts business tasks',s.counts.business>=1);
  ok('summary remains non-trading',s.tradeSignal===false);

  const h=stageHeadline(q[0]);
  ok('headline names next company',h.includes('DDD'));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={
  VERSION,STAGE_ORDER,PRIORITY_ORDER,normalizeItem,routeStage,taskType,
  urgency,buildTask,dedupe,buildQueue,nextTask,summary,stageHeadline,selfTest
};

try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestResearchQueueEngine=Object.freeze(API);

})(engineContext);
